import { useEffect, useState } from "react";
import api from "@/lib/api";

let cached = null;
const listeners = new Set();

const DEFAULTS = {
  primary_color: "#1E3A8A",
  accent_color: "#8B5CF6",
  secondary_color: "#0F172A",
  queue_color: "#1E3A8A",
  background_color: "#F6F7FB",
};

/* --- Color utilities --- */
function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return null;
  const h = hex.replace("#", "").trim();
  if (![3, 6].includes(h.length)) return null;
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return null;
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hexToHslString(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { h, s, l } = rgbToHsl(rgb);
  return `${h} ${s}% ${l}%`;
}

function applyCssVariables(branding) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const primary = branding?.primary_color || DEFAULTS.primary_color;
  const accent = branding?.accent_color || DEFAULTS.accent_color;
  const secondary = branding?.secondary_color || DEFAULTS.secondary_color;
  const queue = branding?.queue_color || DEFAULTS.queue_color;
  const bg = branding?.background_color || DEFAULTS.background_color;

  // HSL versions for shadcn/tailwind tokens (hsl(var(--primary)))
  const primaryHsl = hexToHslString(primary);
  const accentHsl = hexToHslString(accent);
  if (primaryHsl) {
    root.style.setProperty("--primary", primaryHsl);
    root.style.setProperty("--ring", primaryHsl);
  }
  if (accentHsl) {
    root.style.setProperty("--accent-foreground", primaryHsl || accentHsl);
  }

  // Raw hex custom vars (for use in inline styles / non-shadcn components)
  root.style.setProperty("--dkc-primary", primary);
  root.style.setProperty("--dkc-accent", accent);
  root.style.setProperty("--dkc-secondary", secondary);
  root.style.setProperty("--dkc-queue", queue);
  root.style.setProperty("--dkc-bg", bg);
}

export function useBranding() {
  const [branding, setBranding] = useState(cached);

  useEffect(() => {
    if (cached) {
      setBranding(cached);
      applyCssVariables(cached);
    }
    const fetchIt = async () => {
      try {
        const { data } = await api.get("/public/settings");
        cached = data;
        setBranding(data);
        applyCssVariables(data);
        listeners.forEach((fn) => fn(data));
      } catch (err) {
        console.warn("[useBranding] fetch failed (using defaults)", err?.response?.status || err?.message);
      }
    };
    if (!cached) fetchIt();
    const listener = (b) => {
      setBranding(b);
      applyCssVariables(b);
    };
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  const refresh = async () => {
    try {
      const { data } = await api.get("/public/settings");
      cached = data;
      applyCssVariables(data);
      listeners.forEach((fn) => fn(data));
    } catch (err) {
      console.warn("[useBranding] refresh failed", err?.response?.status || err?.message);
    }
  };

  return { branding, refresh };
}
